/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	cmpDataEqual,
	CompileContext,
	createEmptyScope,
	createRenderFunctionFromModel,
	DESIGN_CONTEXT_READ_MODE,
	DesignContext,
	ICompileResult,
	importSchema,
	ISchemaConstObject,
	ISchemaImportExport,
	ISchemaValue,
	ISchemaValueModel,
	RUNTIME_CONTEXT_MODE,
	RuntimeContext,
	serializeIDTToYAML,
	TGetBlueprintSchemaModel,
	TGetBlueprintSchemaSpec,
	TSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import {
	BlueprintAction,
	createViewScope,
	IActionDebugData,
	IActionParams,
	TBlueprintActionSchema,
	TBlueprintViewSchema
} from "@adapptio/lib-core";
import { debounce } from "@adapptio/lib-editor-ui";
import {
	createEventEmitter,
	emitEvent,
	eventsHandler,
	offEvent,
	removeAllEventListeners,
	UndoRedoManager
} from "@adapptio/lib-shared";
import { IntegrationService } from "../../../integration/IntegrationService";
import { EditorRenderContext } from "../../../runtime/EditorRenderContext";
import { EditorRoutingManager } from "../../../runtime/EditorRoutingManager";
import { RuntimeService } from "../../../runtime/RuntimeService";
import { IErrorListGroup } from "../../../ui/components/ErrorList/ErrorListTypes";
import { ActionEditSelection } from "./ActionEditSelection";

export interface IActionModelOpts {}

export enum ACTION_MODEL_MODE {
	EDIT,
	PREVIEW,
	PREVIEW_EDIT
}

/**
 * Action Document Model
 */
export class ActionModel {
	/* Contexts */
	private renderCtx: EditorRenderContext<TBlueprintViewSchema>;
	private dCtx: DesignContext;
	private rCtx: RuntimeContext;
	private paramsDesignCtx: DesignContext;
	private paramsRuntimeCtx: RuntimeContext;

	/** Selection */
	private selection: ActionEditSelection;

	/** Emitted when parse errors has changed */
	public onParseErrors = createEventEmitter<void>();

	/** Emitted when runtime context has rendered */
	public onRender = createEventEmitter<void>();

	/** Emitted when blueprint model has changed */
	public onModelChange = createEventEmitter<void>();

	/** Routing manager */
	private routingManager: EditorRoutingManager;

	/** Emitted when new params model has been built */
	public onParamsModelBuilt = createEventEmitter<void>();

	/** Emitted when params model has changed */
	public onParamsModelChange = createEventEmitter<void>();

	public readonly onScopeChange = createEventEmitter();

	/** Blueprint model */
	private model: TGetBlueprintSchemaModel<TBlueprintActionSchema> = null;

	/** If render was requested */
	private renderRequested = false;

	/** Emitted when runtime errors has changed */
	public onRuntimeErrors = createEventEmitter<void>();

	/** Last exported params data - to detect params change */
	private lastParamsSchemaData: ISchemaImportExport = null;

	/** Action parameters model */
	private paramsModel: ISchemaValueModel<ISchemaConstObject<TSchemaConstObjectProps>> = null;
	private paramsModelError?: Error;
	private paramsData?: unknown = {};

	/** Rendered model spec */
	private modelSpec: TGetBlueprintSchemaSpec<TBlueprintViewSchema> = null;

	/** Undo/Redo manager */
	public undoRedoManager = new UndoRedoManager({ historyLimit: 10 });
	private lastSnapshot = null;

	/* Resource event handlers */
	private handleGlobalsContainerChange: () => void;

	private readonly eventsHandler = eventsHandler();

	/**
	 * Document constructor
	 */
	public constructor(
		private runtimeSvc: RuntimeService,
		private integrationSvc: IntegrationService,
		private opts: IActionModelOpts
	) {
		this.routingManager = runtimeSvc.createRoutingManager();
		this.selection = new ActionEditSelection();

		const runtimeResolvers = runtimeSvc.getBaseResolvers();
		const integrationResolvers = integrationSvc.getBaseResolvers();

		this.renderCtx = runtimeSvc.createRenderContext({
			mode: RUNTIME_CONTEXT_MODE.EDITOR,
			mediaResolution: "screenBase",
			routingManager: this.routingManager,
			getComponentListSpec: (spec) => spec?.spec?.content,
			eventLogFn: this.logComponentEvent.bind(this),
			withPrivateConstants: true
		});

		this.dCtx = new DesignContext({
			resolvers: {
				actionRef: runtimeResolvers.actionRef,
				function: runtimeResolvers.function,
				translate: runtimeResolvers.translate,
				viewRef: runtimeResolvers.viewRef,
				...integrationResolvers
			},
			readMode: DESIGN_CONTEXT_READ_MODE.FULL,
			enableCompletition: true
		});

		this.paramsDesignCtx = new DesignContext({
			resolvers: {},
			readMode: DESIGN_CONTEXT_READ_MODE.SCAN,
			enableCompletition: true
		});

		this.paramsRuntimeCtx = new RuntimeContext<TBlueprintActionSchema>(
			{
				mode: RUNTIME_CONTEXT_MODE.EDITOR,
				inEditor: true,
				resolvers: {}
			},
			null,
			createEmptyScope()
		);

		this.rCtx = this.renderCtx.getRuntimeContext();

		this.eventsHandler.subscribe(this.dCtx.modelChangeEvent, () => {
			// console.log("Model has changed", ev);
			this.createSnapshot();
			this.handleModelChange();
		});

		this.eventsHandler.subscribe(this.paramsDesignCtx.modelChangeEvent, (ev) => {
			// console.log("Params model has changed", ev);
			this.createSnapshot();
			this.refreshParamsData();
			emitEvent(this.onParamsModelChange);
		});

		this.eventsHandler.subscribe(this.rCtx.renderEvent, (spec) => {
			this.modelSpec = spec;
			this.buildParamsModel();
			emitEvent(this.onRender);
		});

		this.eventsHandler.subscribe(
			this.renderCtx.getGlobalsContainer().onChange,
			(this.handleGlobalsContainerChange = () => {
				this.updateScope(true);
			})
		);

		this.eventsHandler.subscribe(this.rCtx.errorEvent, () => {
			emitEvent(this.onRuntimeErrors);
		});
	}

	private async initModel(): Promise<void> {
		this.paramsRuntimeCtx.setRenderFunction(createRenderFunctionFromModel(this.model));

		this.createSnapshot();
		this.updateScope(false);
		this.handleModelChange();
	}

	/**
	 * Creates an empty action blueprint
	 */
	public async createDefault(): Promise<void> {
		this.model = BlueprintAction.createDefault(this.dCtx, null);

		await this.dCtx.waitForPendingOperations();
		await this.initModel();
	}

	/**
	 * Loads document
	 *
	 * @param blueprintContent Blueprint YAML contents
	 */
	public async loadDocument(blueprintContent: string): Promise<void> {
		this.dCtx.clearAllParseErrors();

		this.model = this.dCtx.loadYAMLDocument(
			BlueprintAction,
			blueprintContent,
			"local://action"
		);
		await this.dCtx.waitForPendingOperations();

		emitEvent(this.onParseErrors);

		await this.initModel();
	}

	/**
	 * Returns document as YAML
	 */
	public exportDocumentAsYAML(): string {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const idt = this.model.schema.serialize(this.model, [ "$" ]);
		return serializeIDTToYAML(idt);
	}

	/**
	 * Compiles model to native code
	 */
	public compile(): ICompileResult {
		if (!this.model) {
			throw new Error("Model is not initialized.");
		}

		const cCtx = new CompileContext({
			resolvers: this.renderCtx.getResolvers()
		});

		return cCtx.compileModel(this.model);
	}

	private logComponentEvent(eventType, opts, metaData, result) {
		if (window.APP_DEBUG) {
			// eslint-disable-next-line max-len
			console.groupCollapsed(
				"[EventSystem] Handled event '%s' #%d node ID '%s' (%s)",
				metaData.eventName,
				metaData.executionOrder,
				metaData.nodeId,
				eventType
			);
			console.log("Options:", opts);
			console.log("Result:", result);
			console.log("Sender:", {
				id: metaData.senderId,
				path: metaData.senderPath,
				modelNodeId: metaData.senderModelNodeId,
				instance: metaData.senderInstance
			});
			console.log("Input Scope:", metaData.inputScope);
			console.groupEnd();
		}
	}

	/**
	 * Returns last rendered spec (or null)
	 */
	public getLastSpec(): TGetBlueprintSchemaSpec<TBlueprintViewSchema> {
		return this.modelSpec;
	}

	private updateScope(triggerRender: boolean) {
		const scope = createViewScope(this.rCtx, this.paramsData ?? {});
		this.rCtx.setScope(scope, triggerRender);
		emitEvent(this.onScopeChange);
	}

	public getMode(): ACTION_MODEL_MODE {
		const renderCtxMode = this.renderCtx.mode;
		const runtimeCtxMode = this.rCtx.getMode();

		if (renderCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
			if (runtimeCtxMode === RUNTIME_CONTEXT_MODE.EDITOR) {
				return ACTION_MODEL_MODE.EDIT;
			}

			return ACTION_MODEL_MODE.PREVIEW_EDIT;
		}

		return ACTION_MODEL_MODE.PREVIEW;
	}

	public setMode(mode: ACTION_MODEL_MODE): void {
		let renderCtxMode: RUNTIME_CONTEXT_MODE;
		let runtimeCtxMode: RUNTIME_CONTEXT_MODE;

		switch (mode) {
			case this.getMode():
				return;
			case ACTION_MODEL_MODE.PREVIEW:
				renderCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.NORMAL;
				break;
			case ACTION_MODEL_MODE.EDIT:
				renderCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				runtimeCtxMode = RUNTIME_CONTEXT_MODE.EDITOR;
				break;
			default:
				return;
		}

		this.renderCtx.setMode(renderCtxMode);
		this.rCtx.setMode(runtimeCtxMode, true); // trigger must be true otherwise react part will be desynchronized from spec
		this.renderModel();
	}

	private renderModel() {
		if (this.renderRequested) {
			return;
		}

		this.renderRequested = true;

		setTimeout(() => {
			this.renderRequested = false;

			this.rCtx.clearAllRuntimeErrors();
			this.rCtx.render();
		}, 1);
	}

	/**
	 * Returns design context
	 */
	public getDesignContext(): DesignContext {
		return this.dCtx;
	}

	/**
	 * Returns runtime context
	 */
	public getRuntimeContext(): RuntimeContext<TBlueprintActionSchema> {
		return this.rCtx;
	}

	/**
	 * Returns blueprint model
	 */
	public getModel(): TGetBlueprintSchemaModel<TBlueprintActionSchema> {
		return this.model;
	}

	/**
	 * Returns params model (can be null)
	 */
	public getParamsModel(): ISchemaValueModel<ISchemaConstObject<TSchemaConstObjectProps>> {
		return this.paramsModel;
	}

	/**
	 * Returns params model error if there was any
	 */
	public getParamsModelError(): Error | undefined {
		return this.paramsModelError;
	}

	/**
	 * Returns editor selection instance
	 */
	public getSelection(): ActionEditSelection {
		return this.selection;
	}

	/**
	 * Returns all errors in a format for error console
	 */
	public getErrors(): IErrorListGroup {
		return {
			documentUri: null,
			parseErrors: this.dCtx.getParseDocumentErrors("local://action"),
			runtimeErrors: this.rCtx.getRuntimeErrors(true),
			compileErrors: []
		};
	}

	private undoRedoHandle(snapshot) {
		this.model = BlueprintAction.parse(this.dCtx, snapshot);
		this.paramsRuntimeCtx.setRenderFunction(createRenderFunctionFromModel(this.model));
		this.updateScope(true);
		this.buildParamsModel();
		this.renderModel();
		this.lastSnapshot = snapshot;
		this.selection.deselectAll();

		emitEvent(this.onModelChange);
		emitEvent(this.onParamsModelChange);
		emitEvent(this.onRender);
	}

	private createSnapshot = debounce(() => {
		const prevSnapshot = this.lastSnapshot;
		const currentSnapshot = this.model.schema.serialize(this.model, [ "$" ]);
		this.lastSnapshot = currentSnapshot;

		if (prevSnapshot) {
			this.undoRedoManager.add({
				undo: () => this.undoRedoHandle(prevSnapshot),
				redo: () => this.undoRedoHandle(currentSnapshot),
				disposeUndo: () => {
					/** */
				},
				disposeRedo: () => {
					/** */
				}
			});
			emitEvent(this.onRender);
			emitEvent(this.onParamsModelChange);
		}
	}, 500);

	private handleModelChange() {
		// Re-build params model
		this.buildParamsModel();

		// Check selection
		this.checkSelection();

		emitEvent(this.onModelChange);
	}

	private checkSelection() {
		const items = this.selection.getItems().slice();

		for (let i = 0; i < items.length; i++) {
			if (!this.dCtx.getNodeById(items[i].nodeId)) {
				this.selection.deselectByNodeId(items[i].nodeId);
			}
		}
	}

	/**
	 * Re-builds parameters models based on action blueprint model
	 */
	private buildParamsModel(): void {
		this.paramsRuntimeCtx.render();

		const paramsSchemaData = this.paramsRuntimeCtx.getLastSpec().spec.params;

		if (this.lastParamsSchemaData && cmpDataEqual(paramsSchemaData, this.lastParamsSchemaData)) {
			return;
		}

		try {
			const paramsSchema = importSchema<ISchemaValue<ISchemaConstObject<TSchemaConstObjectProps>>>(paramsSchemaData);

			if (this.paramsModel) {
				const prevDataIdt = this.paramsModel.schema.serialize(this.paramsModel, [ "$" ]);
				this.paramsModel.schema.destroy(this.paramsModel);

				this.paramsModel = paramsSchema.parse(this.paramsDesignCtx, prevDataIdt, this.model);
			} else {
				this.paramsModel = paramsSchema.createDefault(this.paramsDesignCtx, this.model);
			}

			this.refreshParamsData();

			this.paramsModelError = undefined;
		} catch (err) {
			this.paramsModelError = err;
		}

		this.lastParamsSchemaData = paramsSchemaData;

		emitEvent(this.onParamsModelBuilt);
		emitEvent(this.onParamsModelChange);
	}

	private refreshParamsData = debounce(async () => {
		try {
			const paramsData = await this.exportParamsData();
			this.paramsData = { ...(paramsData || {}) };
			this.updateScope(true);
		} catch (err) {
			// err
		}
	}, 500);

	/**
	 * Exports values from params model (eg. test final params)
	 * Returns null if model is not available
	 */
	public async exportParamsData(): Promise<IActionParams> {
		if (!this.paramsModel) {
			return null;
		}

		const rCtx = new RuntimeContext(
			{
				mode: RUNTIME_CONTEXT_MODE.NORMAL,
				inEditor: true,
				resolvers: {}
			},
			createRenderFunctionFromModel(this.paramsModel),
			createEmptyScope()
		);

		await rCtx.renderAsync();

		const paramsData = rCtx.getLastSpec();

		rCtx.destroy();
		return paramsData;
	}

	public clearAllParseErrors(): void {
		this.dCtx.clearAllParseErrors();
		emitEvent(this.onParseErrors);
	}

	/**
	 * Assigns action debug data to flow nodes
	 */
	public assignDebugData(debugData: IActionDebugData): void {
		if (!this.model) {
			return;
		}

		const flowNodeList = this.model.props.spec.props.nodes.value;

		flowNodeList.items.forEach((node) => {
			const nodeDebugData = debugData.nodes[node.id];

			if (nodeDebugData) {
				node.schema.setExecutionResult(
					node,
					{
						scope: nodeDebugData.inputScope,
						resolvedOpts: nodeDebugData.nodeOpts,
						activeOutput: nodeDebugData.output.name,
						outputData: nodeDebugData.output.data,
						outputType: nodeDebugData.output.type,
						debug: nodeDebugData.debug,
						executionTimeInMs: nodeDebugData.executionTimeInMs
					},
					true
				);
			}
		});

		emitEvent(flowNodeList.changeEvent);
	}

	/**
	 * Clears debug data from nodes
	 */
	public clearDebugData(): void {
		if (!this.model) {
			return;
		}

		const flowNodeList = this.model.props.spec.props.nodes.value;

		flowNodeList.items.forEach((node) => {
			node.schema.clearExecutionResult(node, true);
		});

		emitEvent(flowNodeList.changeEvent);
	}

	public dispose(): void {
		this.undoRedoManager.clear();
		this.routingManager.dispose();
		this.eventsHandler.unsubscribe();

		if (this.model) {
			this.model.schema.destroy(this.model);
		}

		if (this.paramsModel) {
			this.paramsModel.schema.destroy(this.paramsModel);
		}

		this.dCtx.destroy();
		this.paramsDesignCtx.destroy();
		this.paramsRuntimeCtx.destroy();
		this.selection.destroy();

		removeAllEventListeners(this.onScopeChange);
		removeAllEventListeners(this.onModelChange);
		removeAllEventListeners(this.onParamsModelBuilt);
		removeAllEventListeners(this.onParamsModelChange);
		removeAllEventListeners(this.onRuntimeErrors);
		removeAllEventListeners(this.onParseErrors);

		offEvent(this.renderCtx.getGlobalsContainer().onChange, this.handleGlobalsContainerChange);
	}
}
